import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'reactstrap';
import { useGetAgencyGSTRequired } from 'slices/auth/selector';
import { Currency } from 'types';
import { ShortlistedSummary } from 'types/campaign/applications';
import { formatAmount, textToTitleCase } from 'utils/helpers';

interface LaunchCampaignCostProps {
    summary: ShortlistedSummary;
    isOpen: boolean;
    onClose: () => void;
    onLaunch: () => void | Promise<void>;
    launchingCampaign?: boolean;
    currency: Currency;
}

const LaunchCampaignCost: React.FC<LaunchCampaignCostProps> = ({
    summary,
    isOpen,
    onClose,
    onLaunch,
    currency,
    launchingCampaign = false,
}) => {
    const [canLaunch, setCanLaunch] = useState(false);
    const [showBreakup, setShowBreakup] = useState(false);
    const [launching, setLaunching] = useState(false);

    const shortfall = Math.abs(summary.cost?.shortfall ?? 0);
    const loadAmount = summary.cost?.load_amount ?? 0;
    const blockAmount = summary.cost?.total ?? 0;
    const walletBalance = summary.wallet_balance ?? 0;
    const breakupItems = summary.cost?.breakup_items ?? [];
    const creatorCount = summary.application_count ?? 0;
    const creatorLabel = creatorCount === 1 ? 'creator' : 'creators';
    const gstRequired = useGetAgencyGSTRequired();

    useEffect(() => {
        setCanLaunch(loadAmount <= 0);
    }, [loadAmount]);

    useEffect(() => {
        if (!isOpen) {
            setShowBreakup(false);
            if (!launchingCampaign) {
                setLaunching(false);
            }
        }
    }, [isOpen, launchingCampaign]);

    const isLaunching = launching || launchingCampaign;

    const handleLaunch = async () => {
        if (!canLaunch || isLaunching) return;
        try {
            setLaunching(true);
            onLaunch();
            onClose();
        } catch {
            // Parent handles errors (e.g. toast); keep modal open for retry.
        } finally {
            setLaunching(false);
        }
    };

    const handleModalClose = () => {
        if (isLaunching) return;
        onClose();
    };

    return (
        <Modal isOpen={isOpen} toggle={handleModalClose} centered className="application-cost-approval-modal">
            <div className="modal-body">
                <h6 className="application-cost-approval-modal-title mb-3">
                    Are you sure you want to start collaboration?
                </h6>
                <p className="mb-0">
                    Starting collaboration will block the required funds from your brand wallet for{' '}
                    <span className="fw-semibold">
                        {creatorCount} shortlisted {creatorLabel}
                    </span>{' '}
                    until they complete delivery.
                </p>
                <div className="block-amount-card mt-3">
                    <div className="d-flex justify-content-between">
                        <div className="w-50">
                            <div className="d-flex flex-column justify-content-between gap-5">
                                <div>
                                    <span className="title">Amount to be Blocked</span>
                                    <h6 className="block-amount">{formatAmount(blockAmount, currency?.code, currency?.symbol, currency?.locale)}</h6>
                                    <button
                                        type="button"
                                        className="btn btn-link text-primary p-0"
                                        style={{ marginTop: '6px' }}
                                        onClick={() => setShowBreakup(!showBreakup)}
                                    >
                                        {showBreakup ? 'Hide Breakup' : 'View Breakup'}
                                    </button>
                                </div>
                                <div>
                                    <span className="wallet-balance-label">
                                        Your Wallet Balance:{' '}
                                        <span
                                            className={`ms-2 wallet-balance-value ${
                                                walletBalance <= 0 ? 'text-danger' : 'text-success'
                                            }`}
                                        >
                                            {formatAmount(walletBalance, currency?.code, currency?.symbol, currency?.locale)}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        {showBreakup ? (
                            <div className="border-left-1 w-50 px-3">
                                <h6>Blocked Amount Breakup</h6>
                                <div className="d-flex flex-column justify-content-between gap-2 breakup-list">
                                    <div className="d-flex flex-column gap-2">
                                        {breakupItems.map((item, index) => (
                                            <div
                                                className="d-flex align-items-center justify-content-between"
                                                key={index}
                                            >
                                                <span className="breakup-item-label">
                                                    {(item.name)}
                                                </span>
                                                <span className="breakup-item-value">
                                                    {item.value ? `${formatAmount(item.value, currency?.code, currency?.symbol, currency?.locale)}` : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                <div className="mt-3">
                    {!canLaunch ? (
                        <div className="custom-alert custom-alert-danger" role="alert">
                            You&apos;re short by{' '}
                            <span className="fw-bold text-danger">{formatAmount(shortfall, currency?.code, currency?.symbol, currency?.locale)}</span>. Please add{' '}
                            <span className="fw-bold">{formatAmount(loadAmount, currency?.code, currency?.symbol, currency?.locale)}</span> {gstRequired ? <small className='fw-8'>(includes 18% GST)</small> : ''} to continue.
                        </div>
                    ) : (
                        <div className="custom-alert custom-alert-info">
                            Funds are reserved, not deducted, and auto-release once creators deliver.
                        </div>
                    )}
                </div>
                <div className="mt-4 d-flex justify-content-between align-items-center gap-4">
                    <button
                        type="button"
                        className="btn btn-gradient-outline w-50"
                        disabled={isLaunching}
                        onClick={handleModalClose}
                    >
                        CANCEL
                    </button>
                    {canLaunch ? (
                        <button
                            type="button"
                            disabled={isLaunching}
                            className="btn btn-gradient w-50"
                            onClick={handleLaunch}
                        >
                            {isLaunching ? 'PLEASE WAIT...' : 'START COLLABORATION'}
                        </button>
                    ) : (
                        <Link
                            target="_blank"
                            to={{ pathname: '/wallet' }}
                            onClick={onClose}
                            className="btn btn-gradient w-50"
                        >
                            ADD FUND
                        </Link>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default LaunchCampaignCost;
